import isEqual from 'react-fast-compare';

import {
  areTypedValuesEquivalent,
  coerceToPrimitive,
  flattenValue,
  isTypedValueEmpty,
} from 'notebook/tables/typed-values/DataTypes';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {extractComparableMinMaxValue} from 'procedures/utils/procedureParameterMinMax';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {
  compareValueToRange,
  isComparableTypedValue,
  isOutOfRange,
  type MinMaxTypedValue,
  type RangeComparison,
} from './PMEIParameterRange';

/**
 * For float and datetime parameters, "matches the setpoint" must be a numeric comparison, using
 * the same normalization (parseTextAsFloat / epoch millis via extractComparableMinMaxValue) that
 * the min/max range check uses — otherwise a value like `37.0` would be graded as differing from a
 * setpoint of `37`, contradicting the range check that treats them as equal. Text, selector, and
 * entity values keep areTypedValuesEquivalent, where representation/id equality is the correct
 * semantic and numeric coercion would be wrong.
 */
function areValuesEquivalentForGrading(a: TypedValue, b: TypedValue): boolean {
  const numericA = numericValueForMatch(a);
  const numericB = numericValueForMatch(b);
  if (numericA != null && numericB != null) {
    return numericA === numericB;
  }
  return areTypedValuesEquivalent(a, b);
}

/** Comparable number for float/datetime values; null for other types or unparseable values. */
function numericValueForMatch(value: TypedValue): number | null {
  if (value.type === FieldType.FLOAT || value.type === FieldType.DATETIME) {
    return extractComparableMinMaxValue(value);
  }
  return null;
}

export enum CellColorGrade {
  NONE = 'none', // no background — empty cell, or >1 setpoint so grading is skipped
  UNPLANNED = 'unplanned', // rule 1 → gray (#F0F1F2)
  MISMATCH = 'mismatch', // rule 2 → yellow (#FCF8D6)
  OUT_OF_RANGE = 'outOfRange', // rule 3 → orange (#FFD8A8); data-parameter value outside min/max
  STRICT_MISMATCH = 'strictMismatch', // rule 3 → orange (#FFD8A8); exact deviation (equipment model/value)
  MATCH = 'match', // rule 4 → no background override
}

/**
 * Compares one confirmed cell value against its single planned reference and returns the grade that
 * value earns: MATCH when it satisfies the plan, or a deviation grade (MISMATCH, OUT_OF_RANGE, or
 * STRICT_MISMATCH) describing how it falls short. Each dimension supplies the comparator that encodes
 * its own rules, so `gradeDimension` stays generic and every grade is chosen here in the module —
 * callers never pass colors.
 */
type GradeComparator = (cellValue: TypedValue, reference: TypedValue) => CellColorGrade;

/**
 * Severity ordering used to pick the worst grade — both across multiple readings within a dimension
 * and across dimensions (`combineGrades`). Orange deviations outrank yellow, which outranks gray,
 * which outranks a plain match; NONE (ungraded) is lowest. OUT_OF_RANGE and STRICT_MISMATCH share a
 * tier because they never arise in the same dimension.
 */
const GRADE_SEVERITY: Record<CellColorGrade, number> = {
  [CellColorGrade.NONE]: 0,
  [CellColorGrade.MATCH]: 1,
  [CellColorGrade.UNPLANNED]: 2,
  [CellColorGrade.MISMATCH]: 3,
  [CellColorGrade.OUT_OF_RANGE]: 4,
  [CellColorGrade.STRICT_MISMATCH]: 4,
};

function moreSevereGrade(a: CellColorGrade, b: CellColorGrade): CellColorGrade {
  return GRADE_SEVERITY[a] >= GRADE_SEVERITY[b] ? a : b;
}

/**
 * The shared grading ladder for a single dimension:
 * - no confirmed values, or more than one reference → NONE (nothing meaningful to grade);
 * - no reference → UNPLANNED (confirmed value present but nothing planned);
 * - otherwise grade every confirmed reading against the single reference with `compare` and keep the
 *   worst grade, so one deviating reading colors the whole cell.
 */
function gradeDimension(
  cellValues: Array<TypedValue | null>,
  referenceValues: Array<TypedValue | null>,
  compare: GradeComparator
): CellColorGrade {
  const nonEmptyCell = cellValues.filter((v): v is TypedValue => v != null && !isTypedValueEmpty(v));
  if (nonEmptyCell.length === 0) {
    return CellColorGrade.NONE;
  }

  const nonEmptyRef = referenceValues.filter((v): v is TypedValue => v != null && !isTypedValueEmpty(v));

  if (nonEmptyRef.length > 1) {
    // A dimension has at most one planned value in practice; with no single target to compare
    // against there is nothing meaningful to grade, so skip.
    return CellColorGrade.NONE;
  }

  if (nonEmptyRef.length === 0) {
    return CellColorGrade.UNPLANNED;
  }

  const reference = nonEmptyRef[0];
  return nonEmptyCell
    .map((v) => compare(v, reference))
    .reduce((worst, grade) => moreSevereGrade(worst, grade), CellColorGrade.MATCH);
}

/**
 * Worst-case aggregation of independently graded dimensions (e.g. a material input's material and
 * amount), using the same severity ordering `gradeDimension` applies across readings.
 */
function combineGrades(...grades: Array<CellColorGrade>): CellColorGrade {
  return grades.reduce((worst, grade) => moreSevereGrade(worst, grade), CellColorGrade.NONE);
}

/** MATCH when the values are equal by representation/id, otherwise MISMATCH (yellow). */
const gradedByValue: GradeComparator = (cellValue, reference) =>
  areTypedValuesEquivalent(cellValue, reference) ? CellColorGrade.MATCH : CellColorGrade.MISMATCH;

/** MATCH when the linked entities share an id (not display name), otherwise MISMATCH (yellow). */
const gradedByEntityId: GradeComparator = (cellValue, reference) =>
  areEntityEquivalentById(cellValue, reference) ? CellColorGrade.MATCH : CellColorGrade.MISMATCH;

/**
 * How a single data-parameter value differs from plan, or null when it satisfies the plan.
 */
export type DataParameterDifference = {kind: 'outOfRange'; comparison: RangeComparison} | {kind: 'mismatch'};

/**
 * Classifies one data-parameter value against its single planned setpoint and optional bounds: a
 * value outside the numeric/datetime range is flagged first (it outranks a plain mismatch), then a
 * value that differs from the setpoint, otherwise null when it satisfies the plan.
 */
export function classifyDataParameterDifference(
  value: TypedValue,
  reference: TypedValue,
  minimum: MinMaxTypedValue,
  maximum: MinMaxTypedValue
): DataParameterDifference | null {
  if (isComparableTypedValue(value)) {
    const comparison = compareValueToRange(value, minimum, maximum);
    if (isOutOfRange(comparison)) {
      return {kind: 'outOfRange', comparison};
    }
  }
  if (!areValuesEquivalentForGrading(value, reference)) {
    return {kind: 'mismatch'};
  }
  return null;
}

function gradedByRange(minimum: MinMaxTypedValue, maximum: MinMaxTypedValue): GradeComparator {
  return (cellValue, reference) => {
    const difference = classifyDataParameterDifference(cellValue, reference, minimum, maximum);
    if (difference == null) {
      return CellColorGrade.MATCH;
    }
    return difference.kind === 'outOfRange' ? CellColorGrade.OUT_OF_RANGE : CellColorGrade.MISMATCH;
  };
}

/**
 * Computes the color grade for a data-parameter cell (Confirmed, Measured) based on how its values
 * relate to the reference (setpoint) values and the optional numeric/datetime range.
 *
 * @param cellValues - The recorded values for the cell (may include empty/null entries).
 * @param referenceValues - The planned (setpoint) values for the parameter.
 * @param minimum - Optional lower bound; null/undefined means unbounded.
 * @param maximum - Optional upper bound; null/undefined means unbounded.
 */
export function computeDataParameterCellColorGrade(
  cellValues: Array<TypedValue | null>,
  referenceValues: Array<TypedValue | null>,
  minimum: MinMaxTypedValue = null,
  maximum: MinMaxTypedValue = null
): CellColorGrade {
  return gradeDimension(cellValues, referenceValues, gradedByRange(minimum, maximum));
}

/**
 * Entity-link equality by underlying ID rather than display name. Two links pointing at different
 * entities that happen to share a display name must grade as a mismatch.
 */
function areEntityEquivalentById(a: TypedValue, b: TypedValue): boolean {
  return isEqual(entityIdOf(a), entityIdOf(b));
}

/**
 * The underlying entity id of a link value (ids, not display names). The comparison basis for
 * `areEntityEquivalentById`.
 */
function entityIdOf(value: TypedValue): unknown {
  return coerceToPrimitive(flattenValue(value), {shouldUseIds: true});
}

/**
 * A material-input value has two dimensions — the material (an entity link or free text) and its
 * optional amount.
 */
export interface MaterialInputGradingValue {
  material: TypedValue;
  amount: TypedValue | null;
}

/**
 * Color grade for a material-input Confirmed cell. The material and amount dimensions are graded
 * independently and combined worst-case (MISMATCH beats UNPLANNED beats MATCH/NONE). The rules
 * differ by material type:
 *
 * Entity materials (`isEntity` true) — the material is compared by entity id (not display name, so
 * two distinct entities sharing a name still flag), while a differing entity amount is deliberately
 * not flagged: its comparator always matches, so the amount can only ever surface as UNPLANNED
 * (planned amount absent, confirmed present) or MATCH, never yellow.
 *
 * Text materials (`isEntity` false) — both the material value and the amount are graded with real
 * equality, so either differing yields MISMATCH.
 *
 * In both cases the cell is left ungraded (NONE/MATCH) when everything matches or the confirmed cell
 * is empty.
 */
export function computeMaterialInputCellColorGrade(
  confirmedValues: Array<MaterialInputGradingValue>,
  plannedValues: Array<MaterialInputGradingValue>,
  isEntity: boolean
): CellColorGrade {
  const materialGrade = gradeDimension(
    confirmedValues.map((v) => v.material),
    plannedValues.map((v) => v.material),
    isEntity ? gradedByEntityId : gradedByValue
  );
  const amountGrade = gradeDimension(
    confirmedValues.map((v) => v.amount),
    plannedValues.map((v) => v.amount),
    isEntity ? amountNotGraded : gradedByValue
  );
  return combineGrades(materialGrade, amountGrade);
}

/**
 * Comparator for the entity-material amount dimension: entity amount differences are deliberately
 * not flagged, so every confirmed amount grades as MATCH. `gradeDimension` can still surface
 * UNPLANNED (planned amount absent, confirmed present) at the dimension level.
 */
const amountNotGraded: GradeComparator = () => CellColorGrade.MATCH;

/**
 * A single equipment value: the equipment link/text itself plus, for entity equipment, the id of
 * the entity's linked model (its "Model" tag). `modelId` is null for text equipment or an entity
 * with no model.
 */
export interface EquipmentGradingValue {
  equipment: TypedValue;
  modelId: string | null;
}

/**
 * Color grade for an equipment Confirmed cell (a single dimension — unlike material inputs there is
 * no amount). The rules differ by the equipment version's data type:
 *
 * Entity equipment (`isEntity` true) — orange STRICT_MISMATCH outranks yellow MISMATCH:
 *   - STRICT_MISMATCH: the confirmed equipment entity's model differs from the planned entity's model.
 *   - MISMATCH: the confirmed equipment entity differs from the planned entity, by id (not name).
 *
 * Text equipment (`isEntity` false) — there is no model, so any difference in the equipment value
 * itself is the strict deviation:
 *   - STRICT_MISMATCH: confirmed value differs from planned value.
 *
 * In both cases `gradeDimension` still yields UNPLANNED (confirmed present, nothing planned) and
 * leaves the cell ungraded (NONE/MATCH) when everything matches or the confirmed cell is empty.
 */
export function computeEquipmentCellColorGrade(
  confirmedValues: Array<EquipmentGradingValue>,
  plannedValues: Array<EquipmentGradingValue>,
  isEntity: boolean
): CellColorGrade {
  return gradeDimension(
    confirmedValues.map((v) => v.equipment),
    plannedValues.map((v) => v.equipment),
    isEntity ? gradedByModelAndEquipment([...plannedValues, ...confirmedValues]) : gradedByStrictValue
  );
}

/** Comparator for text equipment: any difference from the planned value is a strict deviation. */
const gradedByStrictValue: GradeComparator = (cellValue, reference) =>
  areTypedValuesEquivalent(cellValue, reference) ? CellColorGrade.MATCH : CellColorGrade.STRICT_MISMATCH;

/**
 * Comparator for entity equipment. A confirmed entity whose model differs from the planned entity's
 * model is a STRICT_MISMATCH (orange); otherwise a differing entity is a plain MISMATCH (yellow).
 * Models are looked up by equipment-entity id from every equipment value in the row.
 */
function gradedByModelAndEquipment(values: Array<EquipmentGradingValue>): GradeComparator {
  // Key by the TypedValue itself: the comparator receives the same object references that were put
  // into `values` (gradeDimension only filters them), so we can recover each value's modelId
  // directly rather than re-deriving an entity id.
  const modelIdByEquipment = new Map(values.map((v) => [v.equipment, v.modelId] as const));
  return (cellValue, reference) => {
    const confirmedModelId = modelIdByEquipment.get(cellValue) ?? null;
    const plannedModelId = modelIdByEquipment.get(reference) ?? null;
    if (isModelMismatch(confirmedModelId, plannedModelId)) {
      return CellColorGrade.STRICT_MISMATCH;
    }
    return areEntityEquivalentById(cellValue, reference) ? CellColorGrade.MATCH : CellColorGrade.MISMATCH;
  };
}

/**
 * Whether a confirmed equipment entity's model differs from the planned equipment entity's model.
 * With no planned model there is nothing to deviate from, so it is never a mismatch (an absent
 * planned equipment already surfaces as UNPLANNED). When a planned model exists, any confirmed model
 * that differs from it — including a confirmed entity with no model at all — is a mismatch.
 */
function isModelMismatch(confirmedModelId: string | null, plannedModelId: string | null): boolean {
  return plannedModelId != null && confirmedModelId !== plannedModelId;
}
